import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { HexMark, HiveMark, Icon, SimulatedTag, StatusDot } from '@hive/ui';
import { publicApi, type PatronOrderStatus, type PatronVenue } from '../api/client';

type Category = 'Drinks' | 'Sharing plates' | 'Mains';

/** The order this patron placed, kept for the page's lifetime in this tab (survives a reload). */
interface Tracked {
  orderId: string;
  key: string;
}

const STEPS = [
  { status: 'new', label: 'Received' },
  { status: 'preparing', label: 'Being prepared' },
  { status: 'served', label: 'Served' },
] as const;

function loadTracked(slug: string): Tracked | null {
  try {
    const raw = sessionStorage.getItem(`nectar-order:${slug}`);
    return raw ? (JSON.parse(raw) as Tracked) : null;
  } catch {
    return null;
  }
}

/**
 * Nectar patron landing: the QR destination. Deliberately web, no install prompt. Public:
 * no sign-in; the scan is logged with the originating screen for attribution.
 */
export function PatronPage() {
  const { slug = '' } = useParams();
  const [params] = useSearchParams();
  const screenCode = params.get('screen') ?? undefined;
  const [venue, setVenue] = useState<PatronVenue | null>(null);
  const [missing, setMissing] = useState(false);
  const [category, setCategory] = useState<Category>('Drinks');
  const [basket, setBasket] = useState<Record<string, number>>({});
  const [table, setTable] = useState(params.get('table') ?? '');
  const [sent, setSent] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tracked, setTracked] = useState<Tracked | null>(() => loadTracked(slug));
  const [progress, setProgress] = useState<PatronOrderStatus | null>(null);

  // Follow the order until it is served or cancelled.
  useEffect(() => {
    if (!tracked) return;
    let stopped = false;
    const poll = async () => {
      const { data } = await publicApi
        .GET('/v1/public/venues/{venueSlug}/orders/{orderId}', { params: { path: { venueSlug: slug, orderId: tracked.orderId }, query: { key: tracked.key } } })
        .catch(() => ({ data: undefined }));
      if (stopped || !data) return;
      setProgress(data);
      if (data.status === 'served' || data.status === 'cancelled') stopped = true;
    };
    void poll();
    const timer = setInterval(() => !stopped && void poll(), 5000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [slug, tracked]);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      publicApi
        .GET('/v1/public/venues/{venueSlug}', { params: { path: { venueSlug: slug }, query: screenCode ? { screen: screenCode } : {} } })
        .then(({ data }) => {
          if (cancelled) return;
          if (data) setVenue(data);
          else setMissing(true);
        })
        .catch(() => !cancelled && setMissing(true));
    void load();
    const timer = setInterval(load, 30_000);
    // Log the scan once per page open.
    void publicApi.POST('/v1/public/venues/{venueSlug}/scans', { params: { path: { venueSlug: slug } }, body: screenCode ? { screenCode } : {} }).catch(() => undefined);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [slug, screenCode]);

  const items = useMemo(() => venue?.menu?.filter((m) => m.category === category) ?? [], [venue, category]);
  const count = Object.values(basket).reduce((a, b) => a + b, 0);
  const add = (id: string) => setBasket((b) => ({ ...b, [id]: (b[id] ?? 0) + 1 }));

  async function order() {
    if (!table.trim()) return setError('Enter your table number first');
    setBusy(true);
    setError(null);
    try {
      const { data, error } = await publicApi.POST('/v1/public/venues/{venueSlug}/orders', {
        params: { path: { venueSlug: slug } },
        body: {
          tableNo: table.trim(),
          ...(screenCode ? { screenCode } : {}),
          items: Object.entries(basket).map(([menuItemId, quantity]) => ({ menuItemId, quantity })),
        },
      });
      if (data) {
        setSent(data.message);
        setBasket({});
        const next = { orderId: data.orderId, key: data.trackingKey };
        setProgress(null);
        setTracked(next);
        try {
          sessionStorage.setItem(`nectar-order:${slug}`, JSON.stringify(next));
        } catch {
          /* private mode: progress still shows until the page is closed */
        }
      } else setError((error as { message?: string } | undefined)?.message ?? 'Could not send the order');
    } catch {
      setError('Could not reach the venue. Try again, or order at the bar.');
    } finally {
      setBusy(false);
    }
  }

  if (missing) {
    return (
      <main className="hv-root p-page">
        <p className="p-missing">This code does not point to a venue. Ask the bar for help.</p>
      </main>
    );
  }
  if (!venue) return <main className="hv-root p-page" aria-busy="true" />;

  const live = venue.live;
  const offerItem = venue.offer?.menuItemId ? venue.menu?.find((m) => m.id === venue.offer!.menuItemId) : undefined;

  return (
    <main className="hv-root p-page">
      <header className="p-hero hv-on-dark">
        <div className="p-hero__top">
          <div className="p-hero__venue">
            <HexMark label={venue.monogram} size={40} tone="light" />
            <span className="p-hero__name">{venue.name}</span>
          </div>
          <label className="p-table">
            <span className="hv-sr-only">Table number</span>
            <input value={table} onChange={(e) => setTable(e.target.value.slice(0, 16))} placeholder="Table no." inputMode="numeric" />
          </label>
        </div>
        {live ? (
          <div className="p-hero__live">
            <span className="p-hero__eyebrow">
              <StatusDot status="live" glow />
              Live{venue.screenName ? ` on the ${venue.screenName.toLowerCase()}` : ''}
              {live.clock ? ` · ${live.clock}` : ''}
            </span>
            <span className="p-hero__match">
              {live.score && live.homeShort ? `${live.title.split(' vs ')[0]} ${live.score} ${live.title.split(' vs ')[1] ?? ''}` : live.title}
              {live.scoreSource === 'simulated' && (live.score || live.clock) && <SimulatedTag title="Score and clock are simulated" />}
            </span>
            <span className="p-hero__sub">{live.competition}</span>
          </div>
        ) : (
          <div className="p-hero__live">
            <span className="p-hero__match">{venue.location ?? 'Welcome'}</span>
          </div>
        )}
      </header>

      <div className="p-body">
        {progress ? (
          <section aria-label="Your order" className="hv-card p-track" role="status">
            <span className="p-track__title">
              Table {progress.tableNo} · {progress.items.reduce((n, i) => n + i.quantity, 0)} item{progress.items.reduce((n, i) => n + i.quantity, 0) === 1 ? '' : 's'}
            </span>
            {progress.status === 'cancelled' ? (
              <p className="p-track__cancelled">The bar cancelled this order. Ask a member of staff if you were expecting it.</p>
            ) : (
              <ol className="p-track__steps">
                {STEPS.map((step, i) => {
                  const at = STEPS.findIndex((x) => x.status === progress.status);
                  return (
                    <li key={step.status} className={i < at ? 'p-track__step p-track__step--done' : i === at ? 'p-track__step p-track__step--now' : 'p-track__step'}>
                      {step.label}
                    </li>
                  );
                })}
              </ol>
            )}
          </section>
        ) : (
          sent && (
            <p className="p-sent" role="status">
              {sent}
            </p>
          )
        )}
        {venue.offer && (
          <section aria-label="Match-night offer" className="hv-card p-offer">
            <span aria-hidden="true" className="p-offer__art">
              <Icon name="cup" size={36} />
            </span>
            <div className="p-offer__body">
              <span className="p-offer__eyebrow">
                <span>{venue.offer.sponsorName ? 'Sponsor offer' : 'Tonight'}</span>
                {venue.offer.endsLabel && <span className="p-offer__ends">{venue.offer.endsLabel}</span>}
              </span>
              <span className="p-offer__title">{venue.offer.title}</span>
              {venue.offer.body && <span className="hv-muted p-small">{venue.offer.body}</span>}
              {offerItem && (
                <div className="p-offer__buy">
                  <span className="p-price">
                    {offerItem.priceLabel}
                    {venue.menuSource === 'simulated' && <SimulatedTag title="Demo menu and prices" />}
                  </span>
                  <button type="button" className="hv-btn hv-btn--primary hv-btn--sm" onClick={() => add(offerItem.id)}>
                    Add
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        <section aria-label="Menu" className="p-menu">
          <div className="p-chips">
            {(['Drinks', 'Sharing plates', 'Mains'] as Category[]).map((c) => (
              <button key={c} type="button" className="hv-chip" aria-pressed={category === c} onClick={() => setCategory(c)}>
                {c}
              </button>
            ))}
          </div>
          <div className="hv-card p-list">
            {items.map((m) => (
              <div key={m.id} className="p-list__row">
                <span className="p-list__name">{m.name}</span>
                <span className="p-price">{m.priceLabel}</span>
                <button type="button" className="hv-btn hv-btn--outline-accent hv-btn--sm" onClick={() => add(m.id)}>
                  {basket[m.id] ? `Add · ${basket[m.id]}` : 'Add'}
                </button>
              </div>
            ))}
          </div>
          {venue.menuSource === 'simulated' && (
            <span className="p-note">
              <SimulatedTag title="Demo menu and prices" /> Demo menu: prices and items are placeholders in this prototype
            </span>
          )}
        </section>

        <section aria-label="Today's fixtures" className="p-fixtures">
          <div className="p-section-head">
            <h2 className="hv-section-label">Today's fixtures</h2>
            <span className="p-small p-faint">Screens across the venue</span>
          </div>
          <div className="hv-card p-list">
            {venue.fixtures.map((f) => (
              <div key={f.fixtureId} className="p-list__row">
                <StatusDot status={f.status === 'live' ? 'live' : 'upcoming'} glow={f.status === 'live'} />
                <span className="p-list__text">
                  <span className="p-list__name">{f.title}</span>
                  <span className="hv-muted p-small">{[f.competition, f.screenName].filter(Boolean).join(' · ')}</span>
                </span>
                <span className={f.status === 'live' ? 'p-live' : 'hv-muted p-small'}>
                  {f.status === 'live'
                    ? 'Live'
                    : f.startsAt
                      ? new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(f.startsAt))
                      : ''}
                </span>
              </div>
            ))}
          </div>
        </section>
      </div>

      <footer className="p-footer">
        {error && (
          <p className="k-inline-error" role="alert">
            {error}
          </p>
        )}
        <button type="button" className="hv-btn hv-btn--primary p-order" disabled={!count || busy} onClick={order}>
          <span>{busy ? 'Sending…' : table ? `Order to table ${table}` : 'Order to your table'}</span>
          <span className="p-order__count">
            {count} item{count === 1 ? '' : 's'}
          </span>
        </button>
        <span className="p-footer__by">
          <HiveMark size={16} />
          Table service by Nectar · pay at the table
        </span>
      </footer>
    </main>
  );
}
